import Vue from 'vue';
import KanbanViewsAPI from '../../api/kanbanViews';
import ConversationApi from '../../api/inbox/conversation';
import ContactAPI from '../../api/contacts';

export const state = {
  views: [],
  columns: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isDeleting: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getViews(_state) {
    return _state.views;
  },
  getColumns(_state) {
    return _state.columns;
  },
  getColumnConversations: _state => columnValue => {
    return _state.columns[columnValue]?.conversations || [];
  },
  getColumnMeta: _state => columnValue => {
    const col = _state.columns[columnValue];
    return {
      loading: col?.loading || false,
      page: col?.page || 1,
      hasMore: col?.hasMore !== false,
      totalCount: col?.totalCount || 0,
    };
  },
};

export const actions = {
  fetchViews: async ({ commit }) => {
    commit('SET_KANBAN_UI_FLAG', { isFetching: true });
    try {
      const response = await KanbanViewsAPI.getKanbanViews();
      commit('SET_KANBAN_VIEWS', response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit('SET_KANBAN_UI_FLAG', { isFetching: false });
    }
  },

  createView: async ({ commit }, viewData) => {
    commit('SET_KANBAN_UI_FLAG', { isCreating: true });
    try {
      const response = await KanbanViewsAPI.create(viewData);
      commit('ADD_KANBAN_VIEW', response.data);
      return response.data;
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    } finally {
      commit('SET_KANBAN_UI_FLAG', { isCreating: false });
    }
  },

  deleteView: async ({ commit }, id) => {
    commit('SET_KANBAN_UI_FLAG', { isDeleting: true });
    try {
      await KanbanViewsAPI.deleteKanbanView(id);
      commit('DELETE_KANBAN_VIEW', id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit('SET_KANBAN_UI_FLAG', { isDeleting: false });
    }
  },

  fetchColumnConversations: async (
    { commit },
    { columnValue, attributeKey, attributeModel, page = 1 }
  ) => {
    commit('SET_COLUMN_LOADING', { columnValue, loading: true });
    try {
      const filterPayload = {
        payload: [
          {
            attribute_key: attributeKey,
            filter_operator: 'equal_to',
            values: [columnValue],
            custom_attribute_type: attributeModel,
            query_operator: null,
          },
        ],
      };

      let items = [];
      let totalCount = 0;

      const CONTACT_PAGE_SIZE = 15;
      const CONVERSATION_PAGE_SIZE = 25;

      if (attributeModel === 'contact_attribute') {
        const { data } = await ContactAPI.filter(page, 'name', filterPayload);
        items = data.payload || [];
        totalCount = data.meta?.count || 0;
      } else {
        const { data } = await ConversationApi.filter({
          queryData: filterPayload,
          page,
        });
        items = data.payload || [];
        totalCount = data.meta?.all_count || 0;
      }

      const pageSize =
        attributeModel === 'contact_attribute'
          ? CONTACT_PAGE_SIZE
          : CONVERSATION_PAGE_SIZE;
      commit('SET_COLUMN_CONVERSATIONS', {
        columnValue,
        conversations: items,
        page,
        totalCount,
        pageSize,
      });
    } catch (error) {
      // Ignore error
    } finally {
      commit('SET_COLUMN_LOADING', { columnValue, loading: false });
    }
  },

  updateConversationAttribute: async (
    _context,
    { conversationId, attributeKey, newValue, existingAttrs }
  ) => {
    await ConversationApi.updateCustomAttributes({
      conversationId,
      customAttributes: { ...existingAttrs, [attributeKey]: newValue },
    });
  },

  updateContactAttribute: async (
    _context,
    { contactId, attributeKey, newValue }
  ) => {
    await ContactAPI.update(contactId, {
      custom_attributes: { [attributeKey]: newValue },
    });
  },

  clearColumns: ({ commit }) => {
    commit('CLEAR_KANBAN_COLUMNS');
  },
};

export const mutations = {
  SET_KANBAN_UI_FLAG(_state, data) {
    _state.uiFlags = { ..._state.uiFlags, ...data };
  },

  SET_KANBAN_VIEWS(_state, views) {
    _state.views = views;
  },

  ADD_KANBAN_VIEW(_state, view) {
    _state.views.push(view);
  },

  DELETE_KANBAN_VIEW(_state, id) {
    _state.views = _state.views.filter(v => v.id !== id);
  },

  SET_COLUMN_LOADING(_state, { columnValue, loading }) {
    if (!_state.columns[columnValue]) {
      Vue.set(_state.columns, columnValue, {
        conversations: [],
        loading,
        page: 1,
        hasMore: true,
        totalCount: 0,
      });
    } else {
      Vue.set(_state.columns[columnValue], 'loading', loading);
    }
  },

  SET_COLUMN_CONVERSATIONS(
    _state,
    { columnValue, conversations, page, totalCount, pageSize = 25 }
  ) {
    const existing = _state.columns[columnValue];
    const existingConversations =
      page > 1 && existing ? existing.conversations : [];
    Vue.set(_state.columns, columnValue, {
      conversations: [...existingConversations, ...conversations],
      loading: false,
      page,
      hasMore: conversations.length >= pageSize,
      totalCount,
    });
  },

  MOVE_CONVERSATION(
    _state,
    { conversationId, fromColumn, toColumn, conversation }
  ) {
    // Remove from source column
    const sourceCol = _state.columns[fromColumn];
    if (sourceCol) {
      const filtered = sourceCol.conversations.filter(
        c => c.id !== conversationId
      );
      Vue.set(_state.columns, fromColumn, {
        ...sourceCol,
        conversations: filtered,
        totalCount: Math.max(0, (sourceCol.totalCount || 0) - 1),
      });
    }
    // Add to target column
    const targetCol = _state.columns[toColumn];
    if (targetCol) {
      Vue.set(_state.columns, toColumn, {
        ...targetCol,
        conversations: [conversation, ...targetCol.conversations],
        totalCount: (targetCol.totalCount || 0) + 1,
      });
    }
  },

  CLEAR_KANBAN_COLUMNS(_state) {
    _state.columns = {};
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
